import { useState } from 'react';

import type {
  HitlActionRequest,
  HitlActionType,
  HitlItem,
} from '../services/types';

import { Button } from './Button';

export interface QualityGateCardProps {
  hit: HitlItem;
  onAction: (hitId: string, action: HitlActionRequest) => Promise<void>;
  reviewerId: string;
}

type SubPanel = 'mode' | 'constant' | null;

interface CleanserQualityCandidate {
  kind?: string;
  mode_candidates?: string[];
  default_constant?: string;
}

/**
 * v0.6 Stage B12 — UI card for the ``cleanser_quality_missing`` HITL
 * (missing_handler HIGH_MISSING_THRESHOLD).
 *
 * Replaces the legacy option-radio rendering for ``switch_to_mode`` /
 * ``switch_to_constant`` with interactive sub-panels:
 *
 * * **Switch to mode** — radio list of the top-N most-frequent values
 *   from the column (``candidates[0].mode_candidates``); Confirm is
 *   disabled until a candidate is picked.
 * * **Switch to constant** — text input pre-filled with a
 *   type-appropriate default (``candidates[0].default_constant``);
 *   Confirm enabled from the start because the default is a valid
 *   starting point.
 *
 * Both sub-panels expose a ``replace_nulls`` checkbox (unchecked by
 * default). When unchecked, the cleanser leaves genuine ``None`` cells
 * as null and replaces only sentinel-string missing values
 * (``"n/a"`` / ``"?"`` / ...). When checked, ``None`` is also
 * overwritten with the chosen value.
 *
 * Dispatch shapes:
 *   ``{type: "switch_to_mode",     payload: {selected_mode_value, replace_nulls}}``
 *   ``{type: "switch_to_constant", payload: {constant_value,     replace_nulls}}``
 *
 * The reviewer can also pick ``keep_as_is`` (= plain ``accept``) or
 * ``drop_column`` from the auxiliary actions row; those routes are
 * unchanged from the prior HitRow behaviour.
 *
 * Lives outside HitlQueue.tsx so the existing cleanser_quality flow
 * (string_cleaner's adjust_spec, etc.) keeps using the generic HitRow
 * renderer. HitlQueue's routing branch delegates here when
 * ``hit.resolution_type === "cleanser_quality_missing"``.
 */
export function QualityGateCard({
  hit,
  onAction,
  reviewerId,
}: QualityGateCardProps) {
  const candidate =
    (Array.isArray(hit.candidates) && hit.candidates[0]
      ? (hit.candidates[0] as CleanserQualityCandidate)
      : null);
  const modeCandidates: string[] = candidate?.mode_candidates ?? [];
  const defaultConstant: string = candidate?.default_constant ?? 'UNKNOWN';

  const [pending, setPending] = useState<HitlActionType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  const [subPanel, setSubPanel] = useState<SubPanel>(null);
  const [selectedMode, setSelectedMode] = useState<string | null>(null);
  const [constantValue, setConstantValue] = useState<string>(defaultConstant);
  const [replaceNulls, setReplaceNulls] = useState<boolean>(false);

  const dispatch = async (action: HitlActionRequest) => {
    setPending(action.type);
    setError(null);
    try {
      await onAction(hit.hit_id, action);
      setResolved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setPending(null);
    }
  };

  const onConfirm = () => {
    if (subPanel === 'mode' && selectedMode !== null) {
      return dispatch({
        type: 'switch_to_mode',
        payload: {
          selected_mode_value: selectedMode,
          replace_nulls: replaceNulls,
        },
        reviewer_id: reviewerId,
      });
    }
    if (subPanel === 'constant') {
      return dispatch({
        type: 'switch_to_constant',
        payload: {
          constant_value: constantValue,
          replace_nulls: replaceNulls,
        },
        reviewer_id: reviewerId,
      });
    }
  };

  // Confirm gating:
  // * mode panel  → requires a radio pick.
  // * constant    → enabled because the pre-filled default is valid.
  // * no panel    → disabled (reviewer hasn't opened either sub-panel).
  const confirmDisabled =
    pending !== null ||
    subPanel === null ||
    (subPanel === 'mode' && selectedMode === null);

  return (
    <li
      data-rule-card="quality-gate"
      data-resolution-type="cleanser_quality_missing"
      className={`rounded-md border bg-paper border-l-4 border-l-warn border-border p-4 space-y-3 ${
        resolved ? 'opacity-60' : ''
      }`}
      aria-busy={pending !== null}
    >
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h3 className="m-0 text-sm font-semibold flex-1">
            <code>{hit.column}</code> · pick a fill strategy
          </h3>
          <span className="text-xs uppercase font-semibold rounded-sm px-2 py-0.5 bg-surface-alt text-muted whitespace-nowrap">
            quality gate
          </span>
        </div>
        <p className="text-xs text-muted m-0">{hit.issue}</p>
      </div>

      {resolved ? (
        <p className="text-sm text-muted">Resolved.</p>
      ) : (
        <>
          <fieldset className="space-y-2">
            <legend className="text-xs uppercase tracking-wide text-muted">
              How should we treat the missing values?
            </legend>
            <label className="flex items-start gap-3 rounded border border-border p-2 cursor-pointer hover:bg-surface-alt/30">
              <input
                type="radio"
                name={`qg-strategy-${hit.hit_id}`}
                value="mode"
                checked={subPanel === 'mode'}
                onChange={() => setSubPanel('mode')}
                className="mt-0.5"
                data-testid="qg-strategy-mode"
              />
              <span className="flex-1">
                <span className="block font-medium text-sm">
                  Switch to mode
                </span>
                <span className="block text-xs text-muted mt-0.5">
                  Replace missing cells with one of the most-frequent
                  values in the column.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 rounded border border-border p-2 cursor-pointer hover:bg-surface-alt/30">
              <input
                type="radio"
                name={`qg-strategy-${hit.hit_id}`}
                value="constant"
                checked={subPanel === 'constant'}
                onChange={() => setSubPanel('constant')}
                className="mt-0.5"
                data-testid="qg-strategy-constant"
              />
              <span className="flex-1">
                <span className="block font-medium text-sm">
                  Switch to constant
                </span>
                <span className="block text-xs text-muted mt-0.5">
                  Replace missing cells with a single value of your
                  choice (pre-filled with{' '}
                  <code>{defaultConstant}</code>).
                </span>
              </span>
            </label>
          </fieldset>

          {subPanel === 'mode' && (
            <fieldset
              data-testid="qg-mode-subpanel"
              className="space-y-2 border-l-2 border-border pl-3 ml-2"
            >
              <legend className="text-xs uppercase tracking-wide text-muted">
                Pick a value to fill with:
              </legend>
              {modeCandidates.length === 0 ? (
                <p
                  data-testid="qg-mode-empty"
                  className="text-xs text-muted"
                >
                  No non-null values to pick from. Use "Switch to
                  constant" instead.
                </p>
              ) : (
                modeCandidates.map((value) => (
                  <label
                    key={value}
                    className="flex items-start gap-3 rounded border border-border p-2 cursor-pointer hover:bg-surface-alt/30"
                  >
                    <input
                      type="radio"
                      name={`qg-mode-${hit.hit_id}`}
                      value={value}
                      checked={selectedMode === value}
                      onChange={() => setSelectedMode(value)}
                      className="mt-0.5"
                      data-testid={`qg-mode-radio-${value}`}
                    />
                    <span className="flex-1 font-mono text-sm">
                      {value}
                    </span>
                  </label>
                ))
              )}
            </fieldset>
          )}

          {subPanel === 'constant' && (
            <fieldset
              data-testid="qg-constant-subpanel"
              className="space-y-2 border-l-2 border-border pl-3 ml-2"
            >
              <legend className="text-xs uppercase tracking-wide text-muted">
                Fill missing cells with:
              </legend>
              <input
                type="text"
                value={constantValue}
                onChange={(e) => setConstantValue(e.target.value)}
                className="w-full rounded border border-border p-2 font-mono text-sm"
                data-testid="qg-constant-input"
                aria-label="Replacement constant value"
              />
            </fieldset>
          )}

          {subPanel !== null && (
            <label
              className="flex items-start gap-2 text-sm cursor-pointer"
              data-testid="qg-replace-nulls-label"
            >
              <input
                type="checkbox"
                checked={replaceNulls}
                onChange={(e) => setReplaceNulls(e.target.checked)}
                className="mt-0.5"
                data-testid="qg-replace-nulls"
              />
              <span>Also replace null values with this value</span>
            </label>
          )}

          {error && <p className="text-sm text-danger">{error}</p>}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="primary"
              onClick={onConfirm}
              disabled={confirmDisabled}
              data-testid="qg-confirm"
            >
              {pending === 'switch_to_mode' ||
              pending === 'switch_to_constant'
                ? 'Saving…'
                : 'Confirm'}
            </Button>
          </div>
        </>
      )}
    </li>
  );
}
